import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import { SearchInput } from "@/core/components/search-input";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { useGetReferralSources } from "@/referral-sources/query";
import { ReferralSource } from "@/referral-sources/referral-source";
import { ReferralSourcesTable } from "./referral-sources-table";

const ReferralSourcesContent = () => {
  const { businessId } = useParams();
  const { data: referralSources = [], isLoading } =
    useGetReferralSources(businessId);
  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();
  const [searchValue, setSearchValue] = useState("");

  const sortedRecords = useMemo(
    () =>
      [...(referralSources as ReferralSource[])].sort(
        (a, b) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf(),
      ),
    [referralSources],
  );

  const handleRowClick = (referralSource: ReferralSource) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.referralSources,
      mode: SIDEBAR_MODES.view,
      selectedItemId: referralSource.id,
      isOpen: true,
    });
  };

  const handleNewSource = () => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.referralSources,
      mode: SIDEBAR_MODES.new,
      selectedItemId: undefined,
      isOpen: true,
    });
  };

  return (
    <Stack className="mr-5" gap="sm" fullWidth>
      <HeaderStack>
        <PageHeader>Referral Sources</PageHeader>
        <Button variant="secondary" size="sm" onClick={handleNewSource}>
          New Source
        </Button>
      </HeaderStack>
      <MainCard>
        <MainStack className="pt-6">
          <SearchInput
            placeholder="Search Sources"
            value={searchValue}
            onChange={setSearchValue}
          />

          <div className="w-full overflow-auto">
            <ReferralSourcesTable
              data={sortedRecords}
              isLoading={isLoading}
              selectedRowId={selectedItemId}
              onRowClick={handleRowClick}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
        </MainStack>
      </MainCard>
    </Stack>
  );
};

const ReferralSources = () => (
  <HiddenWhenSpiffOnly fallbackPath="../">
    <ReferralSourcesContent />
  </HiddenWhenSpiffOnly>
);

export default ReferralSources;
